import { useCallback, useEffect, useState } from 'react';
import { optimizely } from '../helpers/optimizely';
import { useUniqueUserId } from './useFlags';
import { useCountry } from './useCountry';
import type { ApplyFormSource } from '../helpers/applyFormSource';

// The origins main always accepted the form's conversion message from, plus the form embedded now.
const MAIN_ORIGINS = [
  'http://localhost:3000',
  'https://riders.goflink.com',
  'https://web.fountain.com',
];

export const useSignupConversionTracking = (providerOrigin: string | undefined) => {
  const [isReady, setIsReady] = useState(false);
  const userId = useUniqueUserId();
  const country = useCountry();

  const enableTracking = () => setIsReady(true);

  const trackFormShown = useCallback(
    (provider: ApplyFormSource['provider'], decided: boolean) =>
      optimizely
        .onReady()
        .then(() =>
          optimizely.track(
            'apply_form_shown',
            userId,
            { country },
            { provider, decided: String(decided) },
          ),
        ),
    [userId, country],
  );

  useEffect(() => {
    if (!isReady) return;

    const allowedOrigins = [...MAIN_ORIGINS, providerOrigin];
    const handler = (event: MessageEvent<{ version: string; eventName: string } | null>) => {
      if (!allowedOrigins.includes(event.origin)) return;

      if (event.data?.eventName === 'conversion') {
        console.log('sending conversion event');
        optimizely.track('conversion', userId, { country });
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isReady, providerOrigin, country, userId]);

  return { enableTracking, trackFormShown };
};
